/**
 * Sifa SDK -- public client library for the Sifa AppView on AT Protocol.
 *
 * Pre-1.0: the public API is unstable and may change in any minor release.
 *
 * @see https://github.com/singi-labs/sifa-sdk
 */

declare const __SIFA_SDK_VERSION__: string;

export const SIFA_SDK_VERSION: string = __SIFA_SDK_VERSION__;

export type {
  ActiveApp,
  Endorsement,
  EndorsementData,
  ExternalAccount,
  ExternalAccountKeytraceClaim,
  FeedItem,
  LanguageProficiency,
  LocationValue,
  OrgAddressView,
  OrgFloorVerdict,
  OrgLinkView,
  OrgProfileView,
  RecognizedEntity,
  PdsProviderInfo,
  PresentationLinkView,
  Profile,
  ProfileCertification,
  ProfileCourse,
  ProfileEducation,
  ProfileHonor,
  ProfileIndustry,
  ProfileInvestment,
  ProfileInvestmentLink,
  ProfileInvolvement,
  ProfileInvolvementLink,
  ProfileLanguage,
  ProfileLocation,
  ProfileOverrideSource,
  ProfilePosition,
  ProfilePresentation,
  CoSpeaker,
  ActorCard,
  AuthorSuggestion,
  ProjectMemberCard,
  ProjectRole,
  ProfilePresentationDelivery,
  ProfileProject,
  ProfilePublication,
  ProfileSkill,
  ProfileVolunteering,
  PublicationContributor,
  SkillRef,
  SkillSuggestion,
  TrustStat,
  VerifiedAccount,
  AccountFacetMode,
} from './types/index.js';

// Aggregated public profile view (`id.sifa.getProfileView` XRPC query).
export type {
  ProfileView,
  IndustryDomain,
  ProfileLocationView,
  PositionView,
  EducationView,
  SkillView,
  CertificationView,
  ProjectView,
  VolunteeringView,
  InvolvementView,
  InvolvementLinkView,
  PublicationView,
  ContributorView,
  CourseView,
  DurationView,
  CoSpeakerView,
  ProjectMemberView,
  PresentationDeliveryView,
  PresentationView,
  HonorView,
  LanguageView,
  ExternalAccountView,
  ActiveAppView,
  AtfundLinkView,
} from './types/profile-view.js';

export { PROJECT_ROLES } from './types/index.js';

export {
  ACTIVITY_TIERS,
  APP_CATEGORIES,
  APP_CATEGORY_IDS,
  APP_CATEGORY_MAP,
  CATEGORY_LABELS,
  normalizeSkillCategory,
  CATEGORY_ORDER,
  CONTINENTS,
  COMPANY_OPTIONAL_EMPLOYMENT_TYPES,
  COUNTRIES,
  CALENDAR_EVENT_MODE_LABELS,
  CALENDAR_EVENT_STATUS_LABELS,
  PRESENTATION_LINK_TYPE_LABELS,
  PRESENTATION_LINK_TYPE_OPTIONS,
  PRESENTATION_ROLE_LABELS,
  PRESENTATION_ROLE_OPTIONS,
  PUBLICATION_TYPE_LABELS,
  PUBLICATION_TYPE_OPTIONS,
  EMPLOYMENT_TYPE_GROUPS,
  INVESTMENT_ROLE_OPTIONS,
  INVESTMENT_ROLE_LABELS,
  INVESTMENT_STAGE_OPTIONS,
  INVESTMENT_STAGE_LABELS,
  INVESTMENT_STATUS_OPTIONS,
  INVESTMENT_STATUS_LABELS,
  ON_BEHALF_OF_EMPLOYMENT_TYPES,
  EMPLOYMENT_TYPE_LABELS,
  INDUSTRY_OPTIONS,
  OPEN_TO_OPTIONS,
  OPEN_TO_TOKENS,
  OPEN_TO_TOKEN_TO_VALUE,
  OPEN_TO_VALUE_TO_TOKEN,
  categoryForApp,
  findIndustry,
  getActivityTaxonomyVersion,
  getActivityTier,
  getCalendarEventModeLabel,
  getCalendarEventStatusLabel,
  getPresentationLinkTypeLabel,
  getPresentationRoleLabel,
  getPublicationTypeLabel,
  getAppCategoryIcon,
  getEmploymentTypeLabel,
  isCompanyRequired,
  getInvestmentRoleLabel,
  getInvestmentStageLabel,
  getInvestmentStatusLabel,
  isOnBehalfOfApplicable,
  getIndustryLabelKey,
  getLexiconEntry,
  getOpenToLabelKey,
  normalizeOpenTo,
  openToTokenToValue,
  openToValueToToken,
  getTierMeta,
  getWorkplaceTypeLabel,
  normalizeWorkplaceTypes,
  isAppCategory,
  isKnownAppId,
  PLATFORM_LABELS,
  PLATFORM_OPTIONS,
  SKILL_CATEGORIES,
  WORKPLACE_TYPE_LABELS,
  WORKPLACE_TYPE_OPTIONS,
  dedupeSkills,
  getContinent,
  getFaviconUrl,
  getPlatformLabel,
  groupSkillsByCategory,
  groupSkillsBySubCategory,
  groupSkillsForDisplay,
  isKnownPlatform,
  normalizePlatformId,
  type ActivityTaxonomy,
  type ActivityTier,
  type AppCategoryId,
  type ContinentCode,
  type EmploymentTypeGroup,
  type EmploymentTypeOption,
  type InvestmentOption,
  type IndustryOption,
  type KnownAppId,
  type LexiconEntry,
  type MergedProfileSkill,
  type PresentationLinkTypeOption,
  type PresentationRoleOption,
  type PublicationTypeOption,
  INVOLVEMENT_KIND_OPTIONS,
  INVOLVEMENT_KIND_LABELS,
  INVOLVEMENT_KIND_HEADINGS,
  getInvolvementKindHeading,
  getInvolvementKindLabel,
  ARTIFACT_LINK_KIND_OPTIONS,
  ARTIFACT_LINK_KIND_LABELS,
  getArtifactLinkKindLabel,
  VERIFICATION_PROVIDERS,
  getVerificationProvider,
  isKnownVerificationProvider,
  resolveVerifierProvider,
  primaryVerification,
  type AccountVerification,
  type VerificationProvider,
  type VerificationProviderId,
  type VerificationSource,
  type InvolvementKindOption,
  type ArtifactLinkKindOption,
  type OpenToGroup,
  type OpenToOption,
  type PlatformId,
  type SkillCategory,
  type TierMeta,
  type WorkplaceTypeOption,
} from './taxonomy/index.js';

export {
  certDateExtractor,
  contrastRatio,
  countryCodeToFlag,
  dateRangeExtractor,
  detectPdsProvider,
  formatCompanyName,
  formatDateRange,
  formatDisplayUrl,
  formatDistanceToNow,
  formatLocation,
  formatPresentationDuration,
  formatRelativeTime,
  formatTimelineDate,
  getDisplayLabel,
  getHandleStem,
  getPdsDisplayName,
  isValidRgbColor,
  lexiconDateExtractor,
  limitCombiningMarks,
  locationSegments,
  meetsContrastAA,
  normalizeCompanyKey,
  normalizeLegalForm,
  parseLocationString,
  parseTalkRkey,
  pdsProviderFromApi,
  relativeLuminance,
  resolveEmbed,
  youtubeVideoId,
  youtubeThumbnailUrl,
  rgbToString,
  sanitizeDisplayText,
  normalizeDoi,
  sanitizeHandleInput,
  singleDateExtractor,
  slugifyTitle,
  buildMetaDescription,
  buildTalkSlug,
  sortByDateDesc,
  summarizePresentationDeliveries,
  truncateGraphemes,
  type EmbedResult,
  type PdsProvider,
  type PresentationDeliverySummary,
  type RgbColor,
} from './format/index.js';

export {
  parsePresentationDuration,
  durationFromMinutes,
  parseIntendedAudiences,
  stripHtmlToText,
  normalizePresentationRole,
  normalizePresentationMode,
  presentationCsvRowToRecord,
  presentationDeliveryCsvRowToRecord,
  type CsvRow,
  type ParsedPresentation,
  type ParsedDelivery,
} from './import/index.js';

export {
  ACTIVITY_VISIBILITY_RULES,
  ADULT_CONTENT_LABELS,
  APP_URL_PATTERNS,
  COLLECTION_TO_APP,
  getAppIdForCollection,
  hasAdultContent,
  isVisibleActivityItem,
  resolveCardHealth,
  resolveCardUrl,
  type ActivityItemForUrl,
  type ActivityLabel,
  type AdultContentLabel,
  type AppUrlPatterns,
  type CardHealth,
  type CardHealthStrategy,
} from './cards/index.js';

export {
  PUBLISHERS,
  STANDARD_PUBLISHER_ID,
  getPublisherById,
  getPublisherByHost,
  getPublisherFromSiteUrl,
  type Publisher,
} from './publishers/index.js';

// Shared activity-stream view-model: the normalized, framework-free feed both
// sifa-web (React cards) and page.sifa.id (string-HTML renderer) consume.
export {
  ACTIVITY_VERBS,
  STREAM_VERBS,
  getActivityVerbsVersion,
  streamVerbSchema,
  verbForCollection,
  streamAddressSchema,
  streamAuthorSchema,
  streamCardBodySchema,
  streamCardSubjectSchema,
  streamCardVMSchema,
  streamExternalLinkSchema,
  streamGeoSchema,
  streamMediaSchema,
  streamRichSegmentSchema,
  streamSourceSchema,
  streamThemeSchema,
  toStreamCardVM,
  toStreamCardVMs,
  type ActivityItem,
  type ActivityVerbMap,
  type StreamAddress,
  type StreamAuthor,
  type StreamCardBody,
  type StreamCardSubject,
  type StreamCardVM,
  type StreamExternalLink,
  type StreamGeo,
  type StreamMedia,
  type StreamMediaBase,
  type StreamMediaBlob,
  type StreamMediaResolved,
  type StreamRichSegment,
  type StreamSource,
  type StreamTheme,
  type StreamVerb,
  type ToStreamCardVMOptions,
} from './stream/index.js';

export {
  COMPLETENESS_MAX_SCORE,
  completenessPercent,
  completenessScore,
  DIMENSIONS_MAX_SCORE,
  MIN_SKILLS,
  countFilledDimensions,
  dimensionsFromInputs,
  getFilledDimensionsMap,
  pickPrimaryPosition,
  summarizeProfileView,
  profileToDimensionInputs,
  isPseudoEmployer,
  looksLikeDomain,
  entityDisambiguationLabel,
  searchResultDisambiguation,
  entityResultKey,
  classifyEntityRef,
  isLinked,
  ENTITY_REF_ANCHORS,
  qualifiesAsOrg,
  isRegistrableDomainHandle,
  hasPersonalProfileContent,
  rendersPersonalProfile,
  rendersCompanyProfile,
  resolveAccountFacetMode,
  isCompanyPageIndexable,
  COMPANY_PAGE_MIN_FIRMOGRAPHIC_FIELDS,
  countRecentActivity,
  type DailyActivityCount,
  type DimensionKey,
  type DimensionMap,
  type PrimaryPositionCandidate,
  type ProfileSummary,
  type SummarizeProfileViewOptions,
  type ProfileCompletion,
  type ProfileDimensionInputs,
  type DisambiguationFields,
  type EntityRefAnchor,
  type CompanyFirmographics,
  type PersonalFacetContent,
} from './logic/index.js';

// Profile section model: which sections render, in what order, their nav
// grouping, per-section sorts, and involvement grouping. Shared by the HTML
// profile page, the Markdown/DOCX/print exports, and the personal-site renderer.
export {
  ALL_SECTIONS,
  INVOLVEMENT_HEADING_ORDER,
  SECTION_GROUPS,
  SECTION_LABELS,
  filterHidden,
  getVisibleSectionIds,
  groupInvolvementByHeading,
  hoistPrimary,
  isRoleLineRedundant,
  isSectionPopulated,
  sortByActiveDateRange,
  sortCertifications,
  sortEducation,
  sortHonors,
  sortLanguages,
  sortLanguagesByProficiency,
  sortPositions,
  sortProjects,
  sortPublications,
  visibleItems,
  type InvolvementGroup,
  type SectionGroupId,
  type SectionId,
} from './profile/index.js';

export {
  AtmosphereFeedItemSchema,
  BlobRefSchema,
  EndorsementConfirmationRecordSchema,
  EndorsementRecordSchema,
  OrgProfileRecordSchema,
  OrgEmploymentAttestationRecordSchema,
  EntitySearchResultSchema,
  EntitySearchResponseSchema,
  EntitySelectRequestSchema,
  EntitySelectResponseSchema,
  EntityImportSearchResponseSchema,
  EntityResolveDomainRequestSchema,
  EntityResolveDomainResponseSchema,
  EntityMintDomainResponseSchema,
  FEATURE_FLAGS,
  FeatureAllowlistEntrySchema,
  FeedActorSchema,
  FollowFeedItemSchema,
  FollowFeedPageSchema,
  FollowProfilePageSchema,
  FollowProfileSchema,
  GraphFollowRecordSchema,
  PresentationDurationSchema,
  PresentationLinkSchema,
  ProfileCertificationRecordSchema,
  ProfileCourseRecordSchema,
  ProfileEducationRecordSchema,
  ProfileExternalAccountRecordSchema,
  ProfileHonorRecordSchema,
  ProfileInvestmentRecordSchema,
  InvestmentAmountSchema,
  PROFILE_INVESTMENT_NSID,
  ProfileInvolvementRecordSchema,
  ArtifactLinkSchema,
  PROFILE_INVOLVEMENT_NSID,
  ProfileLanguageRecordSchema,
  ProfilePositionRecordSchema,
  ProfilePresentationDeliveryRecordSchema,
  ProfilePresentationRecordSchema,
  ProfileProjectRecordSchema,
  ProfilePublicationRecordSchema,
  ProfileSelfRecordSchema,
  ProfileSkillRecordSchema,
  ProfileVolunteeringRecordSchema,
  PublicationAuthorSchema,
  SifaFeedItemSchema,
  atUriSchema,
  cidSchema,
  datetimeSchema,
  decodeFeedCursor,
  didSchema,
  encodeFeedCursor,
  externalRecordRefSchema,
  languageTagSchema,
  makeGraphFollowRecordSchema,
  maxGraphemes,
  partialDateSchema,
  selfLabelsSchema,
  skillRefSchema,
  strongRefSchema,
  uriSchema,
  type AtmosphereFeedItem,
  type BlobRef,
  type EndorsementConfirmationRecord,
  type EndorsementRecord,
  type OrgProfileRecord,
  type OrgEmploymentAttestationRecord,
  type EntitySearchResult,
  type EntitySearchResponse,
  type EntitySelectRequest,
  type EntitySelectResponse,
  type EntityImportSearchResponse,
  type EntityResolveDomainRequest,
  type EntityResolveDomainResponse,
  type EntityMintDomainResponse,
  type FeatureAllowlistEntry,
  type FeatureFlag,
  type FeedActor,
  type FeedCursor,
  type FollowFeedItem,
  type FollowFeedPage,
  type FollowProfileItem,
  type FollowProfilePage,
  type GraphFollowRecord,
  type SifaFeedItem,
  type ProfileCertificationRecord,
  type ProfileCourseRecord,
  type ProfileEducationRecord,
  type ProfileExternalAccountRecord,
  type ProfileHonorRecord,
  type ProfileInvolvementRecord,
  type ArtifactLink,
  type ProfileLanguageRecord,
  type PresentationDuration,
  type PresentationLink,
  type ProfilePositionRecord,
  type ProfilePresentationDeliveryRecord,
  type ProfilePresentationRecord,
  type ProfileProjectRecord,
  type ProfilePublicationRecord,
  type ProfileSelfRecord,
  type ProfileSkillRecord,
  type ProfileVolunteeringRecord,
  type PublicationAuthor,
} from './schemas/index.js';

export {
  ROADMAP_ITEM_META,
  ROADMAP_ISSUES_REPO,
  roadmapIssueUrl,
  type RoadmapItemMeta,
} from './roadmap/index.js';

export type {
  LocationSegment,
  LocationSegmentKind,
  LocationSegmentsOptions,
} from './format/index.js';

export type { DisplayUrl, FormatDisplayUrlOptions, UrlPathPolicy } from './format/index.js';

export { SIFA_REPO_GROUPS, describeSifaRecord, repoGroupForCollection } from './repo/index.js';
export type {
  RepoCollectionGroup,
  RepoDeleteOutcome,
  RepoDeleteRecordResult,
  RepoDeleteResult,
  RepoGroupDefinition,
  RepoGroupId,
  RepoInventory,
  RepoRecordLabel,
  RepoRecordSummary,
} from './repo/index.js';
